/* eslint no-param-reassign: "error" */
import { createSlice } from '@reduxjs/toolkit';
import dayjs from '../../dayjs/dayjs_timezone';
import { stringToBoolean } from '../utilities/conversionUtility';
import { convertRegionToISOCode } from '../utilities/localizationUtility';

const initialState = {
  apiVerbose: false,
  bookingFields: [],
  defaultTranslation: {},
  defaultSetting: {},
  depositSettings: null,
  brandingVisualized: false,
  exceededBookingsLimit: false,
  exceededTrialPlan: false,
  messages: {},
  multipleAppointmentsEnabled: false,
  quantityRange: false,
  reservationEnabled: false,
  style: {},
  setting: {},
  translation: {},
  timeZone: window.bta.timeZone || dayjs.tz.guess(),
  urlParams: {},
};

const convertDataType = (setting) => {
  const convertedSettings = { ...setting };
  const booleanKeys = [
    'display_time_range',
    'show_remaining_places',
    'show_price',
    'show_location_chooser',
    'show_resource_chooser',
    'show_location',
    'display_legend',
    'staff_resources',
    'equipment_resources',
    'room_resources',
  ];
  const intKeys = ['when_expired', 'reservation_expiry_minutes'];

  Object.keys(setting).forEach((key) => {
    const value = convertedSettings[key];
    if (booleanKeys.includes(key)) {
      convertedSettings[key] = stringToBoolean(value);
    } else if (intKeys.includes(key)) {
      convertedSettings[key] = parseInt(value, 10);
    }
  });

  return convertedSettings;
};

const filterBookingFieldsByTags = (bookingFields, product) => {
  if (!bookingFields) return [];
  if (typeof bookingFields !== 'string') return bookingFields;
  const fields = JSON.parse(bookingFields);
  const tags = product.tag_list || [];

  return fields.filter((f) => !f.tags || f.tags.length === 0 || f.tags.some((t) => tags.includes(t)));
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setApiVerbose(state, action) {
      state.apiVerbose = action.payload;
    },
    setConfiguration(state, action) {
      const { payload } = action;
      Object.keys(payload).forEach((key) => {
        state[key] = payload[key];
      });
      state.apiVerbose = payload.apiVerbose;
      state.bookingToken = payload.bookingToken;
      state.region = payload.region ? convertRegionToISOCode(payload.region) : 'en';
      state.setting = convertDataType(payload.setting || {});
      state.exceededBookingsLimit = payload.exceededBookingsLimit;
      state.exceededTrialPlan = payload.exceededTrialPlan;
      state.bookingFields = filterBookingFieldsByTags(payload.bookingFields, payload.product);
      state.reservationEnabled = payload.reservationEnabled;
      state.quantityRange = payload.quantityRange;
      state.urlParams = payload.urlParams;
      state.depositSettings = payload.depositSettings;
      state.brandingVisualized = payload.brandingVisualized;
      state.multipleAppointmentsEnabled = payload.multipleAppointmentsEnabled;
      state.securityBondExternalId = payload.securityBondExternalId;
      state.securityBondSettings = payload.securityBondSettings;
      state.rescheduleBookingLocation = payload.rescheduleBookingLocation;
      state.rescheduleBookingVariants = payload.rescheduleBookingVariants;
    },
    setDefaultSetting(state, action) {
      state.defaultSetting = { ...state.defaultSetting, ...action.payload };
    },
    setDefaultTranslation(state, action) {
      state.defaultTranslation = { ...state.defaultTranslation, ...action.payload };
    },
    setSetting(state, action) {
      const { payload } = action;
      state.setting[payload.key] = payload.value;
    },
    setStyle(state, action) {
      state.style = { ...state.style, ...action.payload };
    },
    setTranslation(state, action) {
      const { payload } = action;
      state.translation[payload.key] = payload.value;
    },
    setTimeZone(state, action) {
      state.timeZone = action.payload || window.bta.timeZone;
    },
    setUrlParam: {
      reducer: (state, action) => {
        const { payload } = action;
        state.urlParams[payload.key] = payload.value;
      },
      prepare: (key, value) => ({ payload: { key, value } }),
    },
  },
});

export const settingsActions = settingsSlice.actions;

export default settingsSlice.reducer;
